import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Save, Plus, Trash2, CircleDollarSign, Info, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_NAME = 80;

function blank() {
  return { code: '', symbol: '', name: '', is_default: false };
}

export default function CurrenciesTab() {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const fetchCurrencies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings/currencies`,
        { withCredentials: true });
      const loaded = res.data?.currencies || [];
      setCurrencies(loaded.length ? loaded : [blank()]);
      setMeta({ updated_at: res.data?.updated_at, updated_by: res.data?.updated_by });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load currencies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCurrencies(); }, [fetchCurrencies]);

  const updateField = (idx, field, value) => {
    setCurrencies(list => list.map((c, i) => {
      if (i !== idx) {
        if (field === 'is_default' && value) return { ...c, is_default: false };
        return c;
      }
      let v = value;
      if (field === 'code') v = String(v).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
      else if (field === 'symbol') v = String(v).slice(0, 6);
      else if (field === 'name') v = String(v).slice(0, MAX_NAME);
      return { ...c, [field]: v };
    }));
  };

  const addRow = () => setCurrencies(list => [...list, blank()]);
  const removeRow = (idx) => setCurrencies(list => {
    if (list.length === 1) return [blank()];
    const next = list.filter((_, i) => i !== idx);
    if (!next.some(c => c.is_default) && next.length) next[0].is_default = true;
    return next;
  });

  const save = async () => {
    const cleaned = currencies
      .map(c => ({
        code: (c.code || '').trim().toUpperCase(),
        symbol: (c.symbol || '').trim() || (c.code || '').trim().toUpperCase(),
        name: (c.name || '').trim(),
        is_default: !!c.is_default,
      }))
      .filter(c => c.code && c.name);

    if (!cleaned.length) {
      toast.error('Add at least one currency with a code and name');
      return;
    }
    if (!cleaned.some(c => c.is_default)) cleaned[0].is_default = true;

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/currencies`,
        { currencies: cleaned }, { withCredentials: true });
      toast.success('Currencies saved');
      fetchCurrencies();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save currencies');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading currencies…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="currencies-tab">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[#0F2040] text-base">
            <CircleDollarSign className="w-4 h-4 text-[#14B8A6]" />
            Currencies
          </CardTitle>
          <div className="text-[11px] text-slate-500 flex items-start gap-1.5 mt-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
            Display-only — all internal calculations stay in USD.
            These currency codes / symbols are the list offered for
            the user-profile default, organization default, and
            per-estimate override. The entry marked{' '}
            <Star className="inline w-3 h-3 mx-0.5 fill-amber-500 text-amber-500" />
            {' '}Default is used when no other selection is made.
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {currencies.map((row, idx) => (
            <div key={idx} className="border rounded-lg p-3 bg-white space-y-2"
                 data-testid={`currency-row-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-400 w-5 text-center">
                    {idx + 1}.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant={row.is_default ? 'default' : 'outline'}
                    onClick={() => updateField(idx, 'is_default', !row.is_default)}
                    className={`h-7 text-[10px] ${row.is_default ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300' : ''}`}
                    data-testid={`currency-default-${idx}`}
                  >
                    <Star className={`w-3 h-3 mr-1 ${row.is_default ? 'fill-amber-500 text-amber-500' : ''}`} />
                    {row.is_default ? 'Default' : 'Set default'}
                  </Button>
                </div>
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={() => removeRow(idx)}
                  className="h-7 text-slate-400 hover:text-rose-600"
                  data-testid={`currency-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>

              <div className="grid grid-cols-[auto_auto_1fr] gap-2">
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Code *
                  </label>
                  <Input
                    value={row.code}
                    onChange={e => updateField(idx, 'code', e.target.value)}
                    placeholder="USD"
                    className="h-8 text-xs font-mono w-20"
                    data-testid={`currency-code-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Symbol
                  </label>
                  <Input
                    value={row.symbol}
                    onChange={e => updateField(idx, 'symbol', e.target.value)}
                    placeholder="$"
                    className="h-8 text-xs w-20"
                    data-testid={`currency-symbol-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Name *
                  </label>
                  <Input
                    value={row.name}
                    onChange={e => updateField(idx, 'name', e.target.value)}
                    placeholder="US Dollar"
                    className="h-8 text-xs"
                    data-testid={`currency-name-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between pt-1">
            <Button type="button" size="sm" variant="outline"
              onClick={addRow}
              className="h-7 text-[11px]"
              data-testid="currency-add-btn">
              <Plus className="w-3 h-3 mr-1" />
              Add currency
            </Button>
            <div className="text-[10px] text-slate-400">
              {currencies.length} {currencies.length === 1 ? 'currency' : 'currencies'}
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-200">
            <div className="text-[10px] text-slate-400">
              {meta.updated_at
                ? <>Last saved {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || 'admin'}</>
                : <>Using seeded defaults (USD / AUD / GBP / EUR / CAD) — save to persist edits</>}
            </div>
            <Button type="button" size="sm" onClick={save} disabled={saving}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-7 text-[11px]"
              data-testid="currency-save-btn">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Save currencies
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
